/**
 * KpiCard — a single performance KPI tile with optional sparkline.
 *
 * §5.BT §3.3 P3 Commit C (2026-06-19). Locked in
 *   docs/audits/2026-06-18_5bt_p3_3_dossier_p2_design.md §D.2 + §D.4 + §F
 *
 * Renders a labelled value with an optional Recharts sparkline. Used by the
 * KpiCardGrid for the five scorecard KPIs (Overall Grade, OTD%, Spend,
 * Cost of Poor Performance, Price-YoY).
 *
 * Edge-case contract (P2 §D.4):
 *   - `series.length < 2`             → no sparkline rendered; headline only.
 *   - NULL points in `series`         → rendered as gaps (`connectNulls={false}`).
 *   - Headline value is null/undefined → renders "—" + neutral muted color.
 *   - `partialCoverage` badge          → small "Wingman: partial coverage" chip,
 *                                        tooltip from P2 §F.
 *   - `grade` (Overall Grade card)     → renders large letter with semantic
 *                                        background per gradeColors; F-grade
 *                                        gets the new dangerDeep token.
 */
import React from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { getGradeColor } from './gradeColors';
import { formatValue } from '../../utils/formatValue';

const SPARKLINE_HEIGHT = 40;
// Linear --accent / --border, inlined as literals: SVG stroke is a presentation
// ATTRIBUTE where CSS var() does not resolve, so the sparkline carries the token
// VALUES from SupplierDetail.linear.css (--accent:#3a53df, --border:#e9eaee).
const SPARKLINE_STROKE = '#3a53df';
const SPARKLINE_GRID = '#e9eaee';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): the card chrome
// reads the `.sd` Linear token layer so it flips with the shell in the future
// dark lane. The sparkline stroke/tooltip align to the Linear accent + border;
// the getGradeColor tile is untouched (gradeColors.js protected).
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

// formatValue extracted to frontend/src/utils/formatValue.js (Lane 38 Commit 1).
// KpiCard uses the default precision (pctDigits=1, decimalDigits=1), so call sites
// are unchanged — `formatValue(value, format)` reproduces the prior local behavior exactly.

/**
 * Sparkline rendered inside a KPI card. Renders nothing when fewer than 2
 * non-null points are available (P2 §D.4 degenerate handling).
 */
function Sparkline({ series, accent = SPARKLINE_STROKE }) {
  const cleanCount = (series || []).filter(p => p && p.value !== null && p.value !== undefined).length;
  if (cleanCount < 2) return null;

  return (
    <div data-testid="kpi-sparkline" style={{ marginTop: '10px', height: SPARKLINE_HEIGHT }}>
      <ResponsiveContainer width="100%" height={SPARKLINE_HEIGHT}>
        <LineChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Tooltip
            cursor={{ stroke: SPARKLINE_GRID, strokeWidth: 1 }}
            contentStyle={{
              fontSize: '11px',
              padding: '6px 8px',
              borderRadius: '6px',
              border: `1px solid ${SPARKLINE_GRID}`,
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            }}
            labelFormatter={(_, payload) => {
              if (!payload || !payload.length || !payload[0].payload) return '';
              return payload[0].payload.label || '';
            }}
            formatter={(v) => (v === null || v === undefined ? 'N/A' : v)}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={accent}
            strokeWidth={1.75}
            dot={{ r: 2, fill: accent, stroke: accent }}
            activeDot={{ r: 3 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * KpiCard. Props:
 *   - label       : string — small all-caps card label
 *   - value       : number|string|null — headline value (use `grade` for letter grades)
 *   - format      : 'pct' | 'pct_signed' | 'currency' | 'integer' | 'decimal'
 *   - subtext     : string — optional small line below the headline
 *   - grade       : 'A'..'F' | null — when set, renders a colored letter tile instead of `value`
 *   - series      : [{ label, value }] — optional sparkline data (chronological)
 *   - partialCoverage : bool — when true, renders the P2 §F badge + tooltip
 */
function KpiCard({ label, value, format, subtext, grade, series, partialCoverage }) {
  const gradeStyle = grade ? getGradeColor(grade) : null;

  return (
    // Lane 402: per-card test hook, mirroring the existing
    // `data-testid="drilldown-row" data-cvm=...` / `data-testid="grade-tile"
    // data-grade=...` idiom. Load-bearing for the On-Time-Delivery pins:
    // ScorecardDrilldown renders the SAME premium_ontime_pct field on the SAME
    // tab, so a document-wide assertion is either vacuous or breaks the moment a
    // fixture has a non-empty per_scope. Presentation is unchanged (data-* only).
    <div
      data-testid="kpi-card"
      data-kpi-label={label}
      style={{ ...LINEAR_PANEL, padding: '16px 20px' }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>

      {grade !== undefined && grade !== null ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            data-testid="grade-tile"
            data-grade={grade}
            style={{
              width: '52px',
              height: '52px',
              borderRadius: '12px',
              backgroundColor: gradeStyle.bg,
              color: gradeStyle.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--font-size-hero)',
              fontWeight: 'var(--font-weight-bold)',
              lineHeight: 1,
              border: `1px solid ${gradeStyle.fg}`,
            }}
          >
            {grade}
          </div>
          {subtext && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-3)' }}>
              {subtext}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-hero)',
              fontWeight: 'var(--font-weight-bold)',
              color: value === null || value === undefined ? 'var(--text-3)' : 'var(--text)',
              lineHeight: 1.1,
            }}
          >
            {formatValue(value, format)}
          </div>
          {subtext && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-3)', marginTop: '4px' }}>
              {subtext}
            </div>
          )}
        </div>
      )}

      {/* Lane 134 (spec §2.2): the quiet "partial coverage" badge is promoted
          to words — the caveat was too easy to miss as a chip. */}
      {partialCoverage && (
        <div
          style={{
            marginTop: '8px',
            fontSize: 'var(--font-size-xs)',
            color: '#92400E',
            lineHeight: 1.4,
          }}
          title="Too little overlapping purchase-order data (<20% material overlap) to check this figure for the quarter. Shown as it appears in the scorecard file."
        >
          Wingman could not verify this figure from your purchase orders.
        </div>
      )}

      <Sparkline series={series || []} />
    </div>
  );
}

export default KpiCard;
